import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { WarningCircle, ShieldWarning, CheckCircle } from "@phosphor-icons/react";

/**
 * Compact live-status card that polls /admin/anomalies every 20s.
 * If any IPs are currently over the threshold, a red pulse ring is shown
 * with a click-through table of offenders.
 */
export default function AnomalyWidget() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/admin/anomalies?hours=1&threshold=100");
        if (alive) { setData(r.data); setError(null); }
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Could not load anomalies");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const count = data?.count ?? 0;
  const spiking = count > 0;

  return (
    <div
      data-testid="anomaly-widget"
      className={`card-tactile p-5 relative overflow-hidden ${spiking ? "ring-2 ring-red-300" : ""}`}
    >
      {spiking && (
        <span className="absolute -top-1 -right-1 flex h-3 w-3" aria-hidden>
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
        </span>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-caps flex items-center gap-1.5">
            {spiking ? (
              <ShieldWarning size={14} className="text-red-600" weight="fill" />
            ) : (
              <CheckCircle size={14} className="text-emerald-600" weight="fill" />
            )}
            IP anomalies · last hour
          </div>
          <div className={`font-display text-3xl font-extrabold mt-1 ${spiking ? "text-red-600" : "text-slate-900"}`}>
            {loading ? "…" : count}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            IPs over 100 rate-limit hits · auto-refreshes
          </div>
        </div>
        {count > 0 && (
          <button
            data-testid="anomaly-expand-btn"
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-semibold text-blue-600 hover:underline"
          >
            {expanded ? "Hide" : "View"}
          </button>
        )}
      </div>

      {error && (
        <div className="mt-3 text-xs text-red-600 flex items-center gap-1">
          <WarningCircle size={14} /> {error}
        </div>
      )}

      {expanded && count > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3 space-y-2" data-testid="anomaly-list">
          {(data?.anomalies || []).slice(0, 8).map((a) => (
            <div key={a.ip} className="flex items-start justify-between gap-3 text-sm">
              <div className="min-w-0">
                <div className="font-mono font-semibold text-slate-900 truncate">{a.ip}</div>
                <div className="text-xs text-slate-500 truncate">
                  {(a.paths || []).slice(0, 3).join(" · ") || "—"}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-bold text-red-600">{a.count}</div>
                <div className="text-[10px] text-slate-400">hits</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
